// Registers the service worker and notices when a newer frontend build is deployed.
// A deploy replaces the hashed /build/ files; a tab still running the old entry
// script keeps working until it needs a lazy chunk that is gone. We never reload
// on our own (the user may be mid-form): we raise a flag, the UpdatePrompt shows
// a button, and the user chooses when to reload. "Later" only defers it: the card
// gives way to the slim UpdateBanner, so the reload stays one click away.

import { useSyncExternalStore } from 'react';

const CHECK_EVERY_MS = 15 * 60 * 1000;
const MIN_GAP_MS = 30 * 1000;

let updateAvailable = false;
// "Later" on the card: still outdated, but the card is out of the way.
let updatePostponed = false;
let lastCheck = 0;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function setUpdateAvailable(value: boolean): void {
  if (updateAvailable === value) return;
  updateAvailable = value;
  emit();
}

function setUpdatePostponed(value: boolean): void {
  if (updatePostponed === value) return;
  updatePostponed = value;
  emit();
}

function runningEntry(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/build/app."]');
  return el ? new URL(el.src).pathname : null;
}

async function checkForUpdate(): Promise<void> {
  const running = runningEntry();
  if (!running || updateAvailable || !navigator.onLine) return;
  const now = Date.now();
  if (now - lastCheck < MIN_GAP_MS) return;
  lastCheck = now;
  try {
    const res = await fetch('/api/version', { cache: 'no-store' });
    if (!res.ok) return;
    const { entry } = (await res.json()) as { entry: string | null };
    if (entry && entry !== running) setUpdateAvailable(true);
  } catch {
    // offline or server restarting: try again at the next trigger
  }
}

export function applyUpdate(): void {
  window.location.reload();
}

/** "Later" on the card: the slim header takes over, so reloading stays one click away. */
export function postponeUpdate(): void {
  setUpdatePostponed(true);
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(subscribe, () => updateAvailable);
}

/** True once the person deferred the card: the UpdateBanner is what remains on screen. */
export function useUpdatePostponed(): boolean {
  return useSyncExternalStore(subscribe, () => updatePostponed);
}

export function setupPwa(): void {
  if (import.meta.env.DEV) return;

  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').then((reg) => reg.update()).catch(() => undefined);
    });
  }

  // A lazy chunk that no longer exists: the page is already half broken, so surface the prompt.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    setUpdateAvailable(true);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  });
  window.addEventListener('online', () => void checkForUpdate());
  window.setInterval(() => void checkForUpdate(), CHECK_EVERY_MS);
}
